'use client';

import { GroqImage } from '@/helpers/Image';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { PortableText, PortableTextBlock } from '@portabletext/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';

interface ILastSectionProps {
  title: {
    lineOne: PortableTextBlock[];
    lineTwo: PortableTextBlock[];
  };
  titleMobile: PortableTextBlock[];
  button: GROQButton;
  buttonMobile: GROQButton;
  backgroundImage: GROQImage;
}

export default function LastSection({
  title,
  titleMobile,
  button,
  buttonMobile,
  backgroundImage,
}: ILastSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Transform scroll progress to translateY for parallax effect
  const videoY = useTransform(scrollYProgress, [0, 0.15, 1], [0, 0, 300]);

  return (
    <section
      id="last-section"
      className="pt-[6rem] lg:pt-[15.98rem] bg-black min-h-[79rem] lg:min-h-[182.125rem] relative overflow-clip"
    >
      <div className="container flex flex-col items-end gap-[3rem] lg:gap-[5rem] lg:max-w-[85.5rem] relative z-10">
        {title && (
          <h2 className="prose-headings:text-[5rem] lg:prose-headings:text-[9rem] prose-headings:text-[#dadada] prose-headings:leading-[110%] w-full">
            <motion.span
              className="italic uppercase font-newsreader font-[300] !opacity-50 prose-em:!font-[300]"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <PortableText
                value={title.lineOne}
                components={richTextComponent}
              />
            </motion.span>
            <motion.span
              className="uppercase font-nunito font-[200] text-right"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <PortableText
                value={title.lineTwo}
                components={richTextComponent}
              />
            </motion.span>
          </h2>
        )}
        {titleMobile && (
          <motion.span
            className="wtext-white prose-headings:font-nunito prose-em:!font-newsreader prose-headings:text-[6rem] prose-headings:leading-[120%] prose-headings:uppercase prose-headings:font-[200] prose-headings:mb-0 lg:hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <PortableText value={titleMobile} components={richTextComponent} />
          </motion.span>
        )}
        {button && (
          <CTAButton className="lg:mr-[12rem] hidden lg:block" data={button} />
        )}
        {buttonMobile && (
          <CTAButton className="lg:hidden" data={buttonMobile} />
        )}
      </div>

      <motion.div
        className="absolute w-full h-full left-0 bottom-0 lg:inset-0 z-0 pointer-events-none select-none"
        style={{ y: isMobile ? 0 : videoY }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 1.2,
          ease: [0.76, 0, 0.24, 1],
        }}
      >
        <Image
          src={backgroundImage}
          alt="Background Image"
          layout="fill"
          objectFit="cover"
          objectPosition="center"
          quality={100}
          className="pt-[9rem] lg:pb-[20rem]"
        />
      </motion.div>
    </section>
  );
}
